/**
 * Pagos a Suplidores — migración web de ares8515.
 *
 * Registra pagos a las facturas de cuentas x pagar pendientes de un
 * suplidor: se elige el suplidor, se cargan sus facturas con disponible y el
 * usuario distribuye manualmente el pago entre las facturas (se permiten
 * pagos parciales, nunca por encima del disponible). Al registrar el pago se
 * imprime el comprobante.
 *
 * El disponible que expone el backend ya descuenta los avances (vavance); el
 * escritorio ocultaba ese campo y permitía sobre-pagar. La aplicación de
 * NC/avances no se migró (era código muerto en el escritorio).
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx, StatusPill } from "components/financepro";
import { useApi } from "lib/api";
import { encabezadoEmpresaHtml } from "lib/impresionhtml";

const filtrarSuplidores = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.descrip || ""} ${o.rnc || ""}`),
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function NoRowsFacturas() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Seleccione un suplidor con facturas pendientes
      </Typography>
    </Stack>
  );
}

// Comprobante de pago a suplidor (impresión con iframe oculto).
function imprimirComprobantePago(det, compania) {
  const filas = (det.detalle || [])
    .map(
      (d) =>
        `<tr>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd">${d.nfactura || ""}</td>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
            d.valor
          )}</td>
        </tr>`
    )
    .join("");
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Comprobante de Pago a Suplidor</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:480px;margin:0 auto">
      ${encabezadoEmpresaHtml(compania)}
      <p style="text-align:center;margin:0 0 14px;font-weight:bold">Comprobante de Pago a Suplidor</p>
      <table style="width:100%;border-collapse:collapse;margin-bottom:14px">
        <tr><td style="padding:3px 8px;color:#555"># Pago</td><td style="padding:3px 8px;font-weight:bold">${
          det.pago || ""
        }</td></tr>
        <tr><td style="padding:3px 8px;color:#555">Fecha</td><td style="padding:3px 8px;font-weight:bold">${
          det.fecha || ""
        }</td></tr>
        <tr><td style="padding:3px 8px;color:#555">Suplidor</td><td style="padding:3px 8px;font-weight:bold">${
          det.suplidor || ""
        }</td></tr>
      </table>
      <table style="width:100%;border-collapse:collapse;margin-bottom:8px">
        <tr>
          <th style="padding:3px 6px;text-align:left;border-bottom:1px solid #000">Factura</th>
          <th style="padding:3px 6px;text-align:right;border-bottom:1px solid #000">Valor pagado</th>
        </tr>
        ${filas}
      </table>
      <p style="text-align:right;font-weight:bold;margin:6px 0 14px">TOTAL: RD$ ${fmtMoney(
        det.total
      )}</p>
      <table style="width:100%;border-collapse:collapse;margin-top:10px">
        ${
          det.tipodepago
            ? `<tr><td style="padding:3px 8px;color:#555">Tipo de pago</td><td style="padding:3px 8px;font-weight:bold">${det.tipodepago}</td></tr>`
            : ""
        }
        ${
          det.banco
            ? `<tr><td style="padding:3px 8px;color:#555">Banco</td><td style="padding:3px 8px;font-weight:bold">${det.banco}</td></tr>`
            : ""
        }
        ${
          det.referencia
            ? `<tr><td style="padding:3px 8px;color:#555">Referencia</td><td style="padding:3px 8px;font-weight:bold">${det.referencia}</td></tr>`
            : ""
        }
        ${
          det.descripcion
            ? `<tr><td style="padding:3px 8px;color:#555">Descripción</td><td style="padding:3px 8px;font-weight:bold">${det.descripcion}</td></tr>`
            : ""
        }
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Preparado Por</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function PagosCxp({ embedded = false, onExit, onSaved } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [suplidores, setSuplidores] = useState([]);
  const [cargandoSuplidores, setCargandoSuplidores] = useState(true);
  const [suplidor, setSuplidor] = useState(null);
  const [compania, setCompania] = useState(null);

  const [facturas, setFacturas] = useState([]);
  const [cargandoFacturas, setCargandoFacturas] = useState(false);

  const [tipodepago, setTipodepago] = useState("Efectivo");
  const [banco, setBanco] = useState("");
  const [referencia, setReferencia] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    const cargarSuplidores = async () => {
      setCargandoSuplidores(true);
      try {
        const { ok, data } = await post("/api/cxp_suplidores", {});
        if (ok) setSuplidores(data.suplidores || []);
        else
          showAlert({
            message: data.Error || "No se pudieron cargar los suplidores",
            color: "error",
          });
      } finally {
        setCargandoSuplidores(false);
      }
    };
    cargarSuplidores();
    // Datos de la empresa para el encabezado del comprobante.
    post("/api/cxp_config", {}).then(({ ok, data }) => {
      if (ok) setCompania(data.compania || null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarPendientes = async (sup = suplidor) => {
    if (!sup) {
      setFacturas([]);
      return;
    }
    setCargandoFacturas(true);
    try {
      const { ok, data } = await post("/api/cxp_pendientes", { suplidor: sup.id });
      if (ok) setFacturas((data.facturas || []).map((f) => ({ ...f, apagar: "0" })));
      else
        showAlert({ message: data.Error || "No se pudieron cargar las facturas", color: "error" });
    } finally {
      setCargandoFacturas(false);
    }
  };

  const limpiarPago = () => {
    setTipodepago("Efectivo");
    setBanco("");
    setReferencia("");
    setDescripcion("");
  };

  const limpiar = () => {
    setSuplidor(null);
    setFacturas([]);
    limpiarPago();
  };

  const elegirSuplidor = (v) => {
    setSuplidor(v);
    setFacturas([]);
    limpiarPago();
    if (v) cargarPendientes(v);
  };

  const setAPagar = (id, valor) => {
    setFacturas((prev) => prev.map((f) => (f.id === id ? { ...f, apagar: valor } : f)));
  };

  const validarAPagar = (row) => {
    const monto = toNum(row.apagar);
    if (monto > toNum(row.disponible)) {
      showAlert({
        message: `El valor excede el disponible de la factura ${row.nfactura} (RD$ ${fmtMoney(
          row.disponible
        )})`,
        color: "warning",
      });
      setAPagar(row.id, "0");
    }
  };

  const totalAPagar = useMemo(
    () => facturas.reduce((acc, f) => acc + toNum(f.apagar), 0),
    [facturas]
  );

  const totalDisponibleSuplidor = useMemo(
    () => facturas.reduce((acc, f) => acc + toNum(f.disponible), 0),
    [facturas]
  );

  const pagar = async () => {
    const detalle = facturas
      .filter((f) => toNum(f.apagar) > 0)
      .map((f) => ({ id: f.id, valor: toNum(f.apagar) }));
    if (!suplidor || detalle.length === 0) {
      showAlert({
        message: "Debe indicar el valor a pagar de al menos una factura",
        color: "warning",
      });
      return;
    }
    const excedida = facturas.find((f) => toNum(f.apagar) > toNum(f.disponible));
    if (excedida) {
      showAlert({
        message: `El valor excede el disponible de la factura ${excedida.nfactura}`,
        color: "warning",
      });
      return;
    }
    if (tipodepago !== "Efectivo" && !banco.trim()) {
      showAlert({
        message: "Debe indicar el banco para pagos que no son en efectivo",
        color: "warning",
      });
      return;
    }
    if (tipodepago !== "Efectivo" && !referencia.trim()) {
      showAlert({
        message: "Debe indicar la referencia para pagos que no son en efectivo",
        color: "warning",
      });
      return;
    }
    if (!descripcion.trim()) {
      showAlert({ message: "La descripción del pago no puede estar en blanco", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/cxp_pagar", {
        suplidor: suplidor.id,
        tipodepago,
        banco: banco.trim(),
        referencia: referencia.trim(),
        descripcion: descripcion.trim(),
        detalle,
      });
      if (ok) {
        showAlert({ message: data.ok || "Pago registrado", color: "success" });
        imprimirComprobantePago(
          {
            pago: data.pago,
            fecha: hoyISO(),
            suplidor: suplidor.descrip,
            detalle: facturas
              .filter((f) => toNum(f.apagar) > 0)
              .map((f) => ({ nfactura: f.nfactura, valor: toNum(f.apagar) })),
            total: data.total,
            tipodepago,
            banco: banco.trim(),
            referencia: referencia.trim(),
            descripcion: descripcion.trim(),
          },
          compania
        );
        limpiarPago();
        cargarPendientes();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar el pago", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const columnasFacturas = [
    { field: "nfactura", headerName: "Fact#", width: 110 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    {
      field: "tipofactura",
      headerName: "Tipo",
      width: 150,
      // Una consignada vale solo lo vendido, no el conduce completo: sin
      // avisarlo, el monto parece incompleto frente al papel del suplidor.
      renderCell: (params) =>
        params.row.consignada ? (
          <Tooltip
            title={`Consignación: solo lo vendido. Quedan ${
              params.row.enpiso || 0
            } motores en piso`}
          >
            <span>
              <StatusPill label="Consignado" variant="info" />
            </span>
          </Tooltip>
        ) : (
          params.value
        ),
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "vplicado",
      headerName: "Pagado",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "disponible",
      headerName: "Disponible",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      // Plan de pagos de la factura: lo vencido (clic lo pone en "A pagar")
      // o la próxima cuota. Se arma en el detalle de la factura.
      field: "planvencido",
      headerName: "Plan de pagos",
      width: 190,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const r = params.row;
        if (!r.tieneplan) return <span style={{ color: FP.color.textMuted }}>—</span>;
        const vencido = toNum(r.planvencido);
        const monto = Math.min(
          vencido > 0 ? vencido : toNum(r.planproxima?.pendiente),
          toNum(r.disponible)
        );
        return (
          <Tooltip
            title={
              (r.plandesfasado
                ? "El plan ya no cuadra con la factura; rehágalo en el detalle. "
                : "") + (monto > 0 ? "Clic para pagar este monto" : "")
            }
          >
            <Box
              onClick={() => monto > 0 && setAPagar(r.id, monto.toFixed(2))}
              sx={{ cursor: monto > 0 ? "pointer" : "default", lineHeight: 1.25 }}
            >
              {vencido > 0 ? (
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, color: FP.color.danger }}>
                  Vencido {fmtMoney(vencido)}
                  {r.plancuotasvencidas > 1 ? ` (${r.plancuotasvencidas})` : ""}
                </Typography>
              ) : null}
              {r.planproxima && vencido <= 0 ? (
                <Typography
                  sx={{ fontSize: "0.8rem", fontWeight: 700, color: FP.color.textPrimary }}
                >
                  {r.planproxima.fechatexto} · {fmtMoney(r.planproxima.pendiente)}
                </Typography>
              ) : null}
              {r.plandesfasado ? (
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#92400E" }}>
                  Plan desfasado
                </Typography>
              ) : null}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "apagar",
      headerName: "A pagar",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.apagar}
          onChange={(e) => setAPagar(params.row.id, e.target.value)}
          onBlur={() => validarAPagar(params.row)}
          inputProps={{ style: { textAlign: "right" } }}
          sx={fpInputSx}
        />
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Pagos a Suplidores"
        subtitle="Aplica pagos a las facturas de cuentas x pagar pendientes de un suplidor"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Suplidor y facturas pendientes */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonSearchOutlinedIcon} color={FP.color.accentBlue}>
            Suplidor
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarSuplidores}
                options={suplidores}
                loading={cargandoSuplidores}
                value={suplidor}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.descrip || "")}
                isOptionEqualToValue={(o, v) => (o.id || o) === (v.id || v)}
                onChange={(_, v) => elegirSuplidor(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.descrip}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {o.rnc ? `RNC ${o.rnc}` : ""}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Suplidor (nombre o RNC)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
              {suplidor && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={`RNC ${suplidor.rnc || "—"}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`${facturas.length} facturas — Disponible RD$ ${fmtMoney(
                      totalDisponibleSuplidor
                    )}`}
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                </Stack>
              )}
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={facturas}
              columns={columnasFacturas}
              loading={cargandoFacturas}
              rowHeight={52}
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsFacturas }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>

        {/* Pago */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
            Pago
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="Total a pagar"
                value={fmtMoney(totalAPagar)}
                InputProps={{ readOnly: true }}
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Tipo de pago"
                value={tipodepago}
                onChange={(e) => setTipodepago(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="Efectivo">Efectivo</MenuItem>
                <MenuItem value="Tarjeta de Credito">Tarjeta de Credito</MenuItem>
                <MenuItem value="Transferencia">Transferencia</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="Banco"
                value={banco}
                onChange={(e) => setBanco(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="Referencia"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Descripción"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                startIcon={
                  guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                }
                onClick={pagar}
                disabled={guardando}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Registrar pago e imprimir
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
